import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CContainer,
  CRow,
  CButton,
  CSwitch,
  CDataTable,
  CCollapse,
  CBadge,
  CPagination,
  CTooltip,
} from "@coreui/react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { useIsMobile } from "../hooks/is-mobile";
import {
  IvaEmittedDoc,
  IvaItem,
  IvaOverview,
  IvaReceivedDoc,
  IvaSection,
  IvaSectionPage,
  getIvaOverview,
  getIvaPeriodSection,
  setIvaInclusion,
} from "../api/iva";
import { IVA_INCLUSIONS_UPDATE } from "../auth/permissions";

const PAGE_SIZE = 20;

const MONTH_LABELS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Setiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const fmt = (n: number | null | undefined) => {
  const v = Number(n ?? 0);
  return v.toLocaleString("es-PY", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const fmtIva = (
  value: number | null | undefined,
  currency: string | null | undefined,
  rate: number | null | undefined,
): React.ReactNode => {
  const v = Number(value ?? 0);
  const cur = (currency ?? "PYG").toUpperCase();
  if (cur === "PYG") return <span>₲ {fmt(v)}</span>;
  const r = rate != null ? Number(rate) : null;
  const pyg = r != null ? v * r : null;
  return (
    <span>
      {pyg != null ? `₲ ${fmt(pyg)}` : "—"}{" "}
      <small className="text-muted">
        ({fmt(v)} {cur})
      </small>
    </span>
  );
};

type DocState = "all" | "none" | "partial";

const itemEffective = (doc: IvaEmittedDoc, item: IvaItem): boolean => {
  if (item.itemOverride !== null) return item.itemOverride;
  if (doc.docOverride !== null) return doc.docOverride;
  return true;
};

const emittedDocState = (doc: IvaEmittedDoc): DocState => {
  if (!doc.items.length) {
    const v = doc.docOverride === null ? true : doc.docOverride;
    return v ? "all" : "none";
  }
  let included = 0;
  for (const it of doc.items) {
    if (itemEffective(doc, it)) included++;
  }
  if (included === doc.items.length) return "all";
  if (included === 0) return "none";
  return "partial";
};

// Wrapper that prevents the underlying input from gaining focus on click.
// Without this, clicking a switch deep inside a scrollable container triggers
// the browser's "scroll focused element into view" behavior, which cascades up
// to the window and jumps the page. preventDefault on mousedown blocks the
// focus transfer; the click itself still fires normally.
const NoFocusOnClick = ({ children }: { children: React.ReactNode }) => (
  <span
    style={{ display: "inline-block" }}
    onMouseDown={(e) => e.preventDefault()}
  >
    {children}
  </span>
);

const StatusBadge = ({ state }: { state: DocState }) => {
  const conf = {
    all: { color: "success", label: "Completa" },
    none: { color: "danger", label: "Excluida" },
    partial: { color: "warning", label: "Parcial" },
  }[state];
  return (
    <CBadge
      color={conf.color}
      style={{ minWidth: 78, display: "inline-block" }}
    >
      {conf.label}
    </CBadge>
  );
};

const PeriodCard = ({
  title,
  data,
  onAnalyze,
  analyzing,
}: {
  title: string;
  data: import("../api/iva").IvaPeriodResult | null;
  onAnalyze: () => void;
  analyzing: boolean;
}) => {
  if (!data) return null;
  const debit = (data.debit10 ?? 0) + (data.debit5 ?? 0);
  const credit = (data.credit10 ?? 0) + (data.credit5 ?? 0);
  return (
    <CCard>
      <CCardHeader>
        <strong>{title}</strong>{" "}
        <small className="text-muted">
          {MONTH_LABELS[data.month - 1]} {data.year}
        </small>
      </CCardHeader>
      <CCardBody>
        <CRow>
          <CCol md={4}>
            <div className="text-muted">Débito Fiscal (Emitidos)</div>
            <h4>Gs. {fmt(debit)}</h4>
            <small>
              10%: {fmt(data.debit10)} · 5%: {fmt(data.debit5)}
            </small>
          </CCol>
          <CCol md={4}>
            <div className="text-muted">Crédito Fiscal (Recibidos)</div>
            <h4>Gs. {fmt(credit)}</h4>
            <small>
              10%: {fmt(data.credit10)} · 5%: {fmt(data.credit5)}
            </small>
          </CCol>
          <CCol md={4}>
            <div className="text-muted">IVA a Pagar</div>
            <h3
              className={
                data.netIvaPayable > 0 ? "text-danger" : "text-success"
              }
            >
              Gs. {fmt(data.netIvaPayable)}
            </h3>
            {data.netIvaPayable < 0 && (
              <small className="text-success">Saldo a favor</small>
            )}
          </CCol>
        </CRow>
        <div style={{ marginTop: 12 }}>
          <CButton
            color="primary"
            size="sm"
            onClick={onAnalyze}
            disabled={analyzing}
          >
            {analyzing ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                  style={{ marginRight: 6 }}
                />
                Cargando comprobantes...
              </>
            ) : (
              "Analizar comprobantes"
            )}
          </CButton>
        </div>
      </CCardBody>
    </CCard>
  );
};

const EmittedRow = ({
  doc,
  consolidated,
  onToggleDoc,
  onToggleItem,
}: {
  doc: IvaEmittedDoc;
  consolidated: boolean;
  onToggleDoc: (doc: IvaEmittedDoc) => void;
  onToggleItem: (doc: IvaEmittedDoc, item: IvaItem) => void;
}) => {
  const [open, setOpen] = useState(false);
  const state = emittedDocState(doc);

  return (
    <>
      <tr>
        <td style={{ width: 130 }}>
          <CButton
            size="sm"
            color="link"
            onClick={() => setOpen((v) => !v)}
            style={{ padding: "2px 6px", textDecoration: "none" }}
            title={open ? "Ocultar ítems" : "Ver ítems"}
          >
            <span style={{ display: "inline-block", width: 12 }}>
              {open ? "▾" : "▸"}
            </span>{" "}
            <small>{open ? "Ocultar" : "Ver ítems"}</small>
          </CButton>
        </td>
        <td>
          <div>
            <strong>{doc.documentNumber}</strong>
          </div>
          {doc.counterparty && (
            <small className="text-muted">{doc.counterparty}</small>
          )}
        </td>
        <td>{new Date(doc.documentDate).toLocaleDateString("es-PY")}</td>
        <td className="text-center">
          {fmtIva(doc.iva10Percent, doc.currency, doc.pygExchangeRate)}
        </td>
        <td className="text-center">
          {fmtIva(doc.iva5Percent, doc.currency, doc.pygExchangeRate)}
        </td>
        <td style={{ width: 90 }}>
          <NoFocusOnClick>
            <CSwitch
              color="primary"
              checked={state === "all"}
              onChange={() => onToggleDoc(doc)}
              disabled={consolidated}
            />
          </NoFocusOnClick>
        </td>
        <td style={{ width: 110 }}>
          <StatusBadge state={state} />
        </td>
      </tr>
      <tr>
        <td colSpan={7} style={{ padding: 0, border: "none" }}>
          <CCollapse show={open}>
            <table className="table table-sm" style={{ marginBottom: 0 }}>
              <thead>
                <tr>
                  <th>Ítem</th>
                  <th className="text-center">IVA 10%</th>
                  <th className="text-center">IVA 5%</th>
                  <th className="text-center">Exento</th>
                  <th style={{ width: 90 }}>Incluir</th>
                </tr>
              </thead>
              <tbody>
                {doc.items.map((it) => {
                  const effective = itemEffective(doc, it);
                  return (
                    <tr key={it.itemId}>
                      <td>{it.description}</td>
                      <td className="text-center">
                        {fmtIva(
                          it.iva10Percent,
                          doc.currency,
                          doc.pygExchangeRate,
                        )}
                      </td>
                      <td className="text-center">
                        {fmtIva(
                          it.iva5Percent,
                          doc.currency,
                          doc.pygExchangeRate,
                        )}
                      </td>
                      <td className="text-center">
                        {fmtIva(
                          it.amountExempt,
                          doc.currency,
                          doc.pygExchangeRate,
                        )}
                      </td>
                      <td style={{ width: 90 }}>
                        <NoFocusOnClick>
                          <CSwitch
                            color="primary"
                            checked={effective}
                            onChange={() => onToggleItem(doc, it)}
                            disabled={consolidated}
                          />
                        </NoFocusOnClick>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CCollapse>
        </td>
      </tr>
    </>
  );
};

const ReceivedRow = ({
  doc,
  consolidated,
  onToggle,
}: {
  doc: IvaReceivedDoc;
  consolidated: boolean;
  onToggle: (doc: IvaReceivedDoc) => void;
}) => {
  const docIncluded = doc.docOverride === null ? true : doc.docOverride;
  const state: DocState = docIncluded ? "all" : "none";
  return (
    <tr>
      <td style={{ width: 130 }} />
      <td>
        <div>
          <strong>{doc.documentNumber}</strong>{" "}
          <CBadge color="info">{doc.receivedType}</CBadge>
        </div>
        {doc.counterparty && (
          <small className="text-muted">{doc.counterparty}</small>
        )}
      </td>
      <td>{new Date(doc.documentDate).toLocaleDateString("es-PY")}</td>
      <td className="text-center">
        {fmtIva(doc.iva10Percent, doc.currency, doc.pygExchangeRate)}
      </td>
      <td className="text-center">
        {fmtIva(doc.iva5Percent, doc.currency, doc.pygExchangeRate)}
      </td>
      <td style={{ width: 90 }}>
        <NoFocusOnClick>
          <CSwitch
            color="primary"
            checked={docIncluded}
            onChange={() => onToggle(doc)}
            disabled={consolidated}
          />
        </NoFocusOnClick>
      </td>
      <td style={{ width: 110 }}>
        <StatusBadge state={state} />
      </td>
    </tr>
  );
};

const SectionPagination = ({
  page,
  total,
  pageSize,
  onChange,
}: {
  page: number;
  total: number;
  pageSize: number;
  onChange: (p: number) => void;
}) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <small className="text-muted">
        {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} de{" "}
        {total}
      </small>
      <CPagination
        activePage={page}
        pages={pages}
        onActivePageChange={onChange}
      />
    </div>
  );
};

const SECTION_TITLES: Record<IvaSection, string> = {
  invoices: "Facturas emitidas",
  credit_notes: "Notas de Crédito emitidas",
  debit_notes: "Notas de Débito emitidas",
  received: "Documentos recibidos",
};

type EmittedSection = Exclude<IvaSection, "received">;

const SECTION_TO_DOC_TYPE: Record<
  EmittedSection,
  "invoice" | "credit_note" | "debit_note"
> = {
  invoices: "invoice",
  credit_notes: "credit_note",
  debit_notes: "debit_note",
};

const IvaPage = () => {
  const isMobile = useIsMobile();
  const taxPayerId = useSelector(
    (s: RootState) => s.taxPayer.data.taxPayer.id,
  ) as number | undefined;

  const canUpdateInclusions = useSelector((s: RootState) =>
    s.taxPayerPermissions.permissions.includes(IVA_INCLUSIONS_UPDATE),
  );

  const [overview, setOverview] = useState<IvaOverview | null>(null);
  const [analyze, setAnalyze] = useState<{
    year: number;
    month: number;
  } | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const [emittedPages, setEmittedPages] = useState<
    Record<EmittedSection, IvaSectionPage<IvaEmittedDoc> | null>
  >({ invoices: null, credit_notes: null, debit_notes: null });
  const [receivedPage, setReceivedPage] =
    useState<IvaSectionPage<IvaReceivedDoc> | null>(null);
  const [pageNum, setPageNum] = useState<Record<IvaSection, number>>({
    invoices: 1,
    credit_notes: 1,
    debit_notes: 1,
    received: 1,
  });
  const [sectionLoading, setSectionLoading] = useState<
    Record<IvaSection, boolean>
  >({
    invoices: false,
    credit_notes: false,
    debit_notes: false,
    received: false,
  });

  const reloadOverview = async () => {
    if (!taxPayerId) return;
    const ov = await getIvaOverview(taxPayerId);
    setOverview(ov);
  };

  const loadEmittedSection = async (section: EmittedSection, page: number) => {
    if (!taxPayerId || !analyze) return;
    setSectionLoading((s) => ({ ...s, [section]: true }));
    try {
      const data = await getIvaPeriodSection<IvaEmittedDoc>(
        taxPayerId,
        analyze.year,
        analyze.month,
        section,
        page,
        PAGE_SIZE,
      );
      setEmittedPages((prev) => ({ ...prev, [section]: data }));
    } finally {
      setSectionLoading((s) => ({ ...s, [section]: false }));
    }
  };

  const loadReceivedSection = async (page: number) => {
    if (!taxPayerId || !analyze) return;
    setSectionLoading((s) => ({ ...s, received: true }));
    try {
      const data = await getIvaPeriodSection<IvaReceivedDoc>(
        taxPayerId,
        analyze.year,
        analyze.month,
        "received",
        page,
        PAGE_SIZE,
      );
      setReceivedPage(data);
    } finally {
      setSectionLoading((s) => ({ ...s, received: false }));
    }
  };

  useEffect(() => {
    setOverviewLoading(true);
    reloadOverview().finally(() => setOverviewLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayerId]);

  // When the analyzed period changes, reset pages and load page 1 of each.
  useEffect(() => {
    if (!analyze) {
      setEmittedPages({
        invoices: null,
        credit_notes: null,
        debit_notes: null,
      });
      setReceivedPage(null);
      return;
    }
    setPageNum({ invoices: 1, credit_notes: 1, debit_notes: 1, received: 1 });
    loadEmittedSection("invoices", 1);
    loadEmittedSection("credit_notes", 1);
    loadEmittedSection("debit_notes", 1);
    loadReceivedSection(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyze, taxPayerId]);

  const setPageFor = (section: IvaSection, p: number) => {
    setPageNum((prev) => ({ ...prev, [section]: p }));
    if (section === "received") loadReceivedSection(p);
    else loadEmittedSection(section, p);
  };

  const consolidated = useMemo(() => {
    return (
      emittedPages.invoices?.consolidated ||
      emittedPages.credit_notes?.consolidated ||
      emittedPages.debit_notes?.consolidated ||
      receivedPage?.consolidated ||
      false
    );
  }, [emittedPages, receivedPage]);

  const updateEmittedDocLocal = (
    section: EmittedSection,
    docId: number,
    fn: (d: IvaEmittedDoc) => IvaEmittedDoc,
  ) => {
    setEmittedPages((prev) => {
      const cur = prev[section];
      if (!cur) return prev;
      return {
        ...prev,
        [section]: {
          ...cur,
          items: cur.items.map((d) => (d.documentId === docId ? fn(d) : d)),
        },
      };
    });
  };

  const onToggleDoc = async (section: EmittedSection, doc: IvaEmittedDoc) => {
    if (!taxPayerId) return;
    const docType = SECTION_TO_DOC_TYPE[section];
    const state = emittedDocState(doc);
    const nextIncluded = state !== "all";
    updateEmittedDocLocal(section, doc.documentId, (d) => ({
      ...d,
      docOverride: nextIncluded,
      items: d.items.map((it) => ({ ...it, itemOverride: null })),
    }));
    await setIvaInclusion(
      taxPayerId,
      docType,
      doc.documentId,
      null,
      nextIncluded,
    );
    reloadOverview();
  };

  const onToggleItem = async (
    section: EmittedSection,
    doc: IvaEmittedDoc,
    item: IvaItem,
  ) => {
    if (!taxPayerId) return;
    const docType = SECTION_TO_DOC_TYPE[section];
    const nextIncluded = !itemEffective(doc, item);
    updateEmittedDocLocal(section, doc.documentId, (d) => ({
      ...d,
      items: d.items.map((it) =>
        it.itemId === item.itemId ? { ...it, itemOverride: nextIncluded } : it,
      ),
    }));
    await setIvaInclusion(
      taxPayerId,
      docType,
      doc.documentId,
      item.itemId,
      nextIncluded,
    );
    reloadOverview();
  };

  const onToggleReceived = async (doc: IvaReceivedDoc) => {
    if (!taxPayerId) return;
    const docIncluded = doc.docOverride === null ? true : doc.docOverride;
    const nextIncluded = !docIncluded;
    setReceivedPage((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((rd) =>
              rd.documentId === doc.documentId
                ? { ...rd, docOverride: nextIncluded }
                : rd,
            ),
          }
        : prev,
    );
    await setIvaInclusion(
      taxPayerId,
      "received_document",
      doc.documentId,
      null,
      nextIncluded,
    );
    reloadOverview();
  };

  const historyRows = useMemo(() => overview?.history ?? [], [overview]);

  if (!taxPayerId) {
    return (
      <CContainer fluid>
        <CCard>
          <CCardBody>Seleccione un contribuyente para ver IVA.</CCardBody>
        </CCard>
      </CContainer>
    );
  }

  const isCurrent =
    !!overview &&
    !!analyze &&
    analyze.year === overview.current.year &&
    analyze.month === overview.current.month;

  const anySectionLoading =
    sectionLoading.invoices ||
    sectionLoading.credit_notes ||
    sectionLoading.debit_notes ||
    sectionLoading.received;

  const renderEmittedSection = (section: EmittedSection) => {
    const data = emittedPages[section];
    return (
      <div key={section} style={{ marginBottom: 18 }}>
        <h5>{SECTION_TITLES[section]}</h5>
        {sectionLoading[section] && !data ? (
          <div className="text-muted" style={{ padding: 8 }}>
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
              style={{ marginRight: 6 }}
            />
            Cargando...
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="text-muted" style={{ padding: 8 }}>
            Sin comprobantes en el período.
          </div>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th />
                    <th>Comprobante</th>
                    <th>Fecha</th>
                    <th className="text-center">IVA 10%</th>
                    <th className="text-center">IVA 5%</th>
                    <th style={{ width: 90 }}>Incluir</th>
                    <th style={{ width: 110 }}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((d) => (
                    <EmittedRow
                      key={`${section}-${d.documentId}`}
                      doc={d}
                      consolidated={data.consolidated || !canUpdateInclusions}
                      onToggleDoc={(dd) => onToggleDoc(section, dd)}
                      onToggleItem={(dd, it) => onToggleItem(section, dd, it)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <SectionPagination
              page={pageNum[section]}
              total={data.total}
              pageSize={data.pageSize}
              onChange={(p) => setPageFor(section, p)}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <CContainer fluid style={{ overflowAnchor: "none" }}>
      <CCard>
        <CCardBody>
          <h2 style={{ marginLeft: "15px" }}>IVA</h2>
        </CCardBody>
      </CCard>
      {overviewLoading && <div>Cargando...</div>}
      <div
        style={
          isMobile
            ? { paddingTop: 8, paddingBottom: 8 }
            : {
                position: "sticky",
                top: 0,
                zIndex: 10,
                backgroundColor: "var(--theme-surface-raised)",
                paddingTop: 8,
                paddingBottom: 8,
              }
        }
      >
        <CRow>
          <CCol md={6}>
            <PeriodCard
              title="Mes actual"
              data={overview?.current ?? null}
              analyzing={anySectionLoading && isCurrent}
              onAnalyze={() =>
                overview &&
                setAnalyze({
                  year: overview.current.year,
                  month: overview.current.month,
                })
              }
            />
          </CCol>
          <CCol md={6}>
            <PeriodCard
              title="Mes anterior (a presentar)"
              data={overview?.previous ?? null}
              analyzing={anySectionLoading && !!analyze && !isCurrent}
              onAnalyze={() =>
                overview &&
                setAnalyze({
                  year: overview.previous.year,
                  month: overview.previous.month,
                })
              }
            />
          </CCol>
        </CRow>
      </div>

      {analyze && (
        <CCard>
          <CCardHeader
            style={{
              backgroundColor: isCurrent ? "var(--theme-info-surface)" : "var(--theme-warning-surface)",
              borderLeft: isCurrent ? "4px solid #2980ff" : "4px solid #f9b115",
            }}
          >
            <CBadge
              color={isCurrent ? "primary" : "warning"}
              style={{ marginRight: 8, fontSize: "0.85rem" }}
            >
              {isCurrent ? "MES ACTUAL" : "MES ANTERIOR"}
            </CBadge>
            <strong style={{ fontSize: "1.1rem" }}>
              Análisis de comprobantes — {MONTH_LABELS[analyze.month - 1]}{" "}
              {analyze.year}
            </strong>
            {consolidated && (
              <CBadge color="danger" style={{ marginLeft: 8 }}>
                período consolidado (read-only)
              </CBadge>
            )}
            <CButton
              size="sm"
              color="link"
              style={{ float: "right" }}
              onClick={() => setAnalyze(null)}
            >
              Cerrar
            </CButton>
          </CCardHeader>
          <CCardBody>
            {renderEmittedSection("invoices")}
            {renderEmittedSection("credit_notes")}
            {renderEmittedSection("debit_notes")}

            <div style={{ marginBottom: 18 }}>
              <h5>{SECTION_TITLES.received}</h5>
              {sectionLoading.received && !receivedPage ? (
                <div className="text-muted" style={{ padding: 8 }}>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                    style={{ marginRight: 6 }}
                  />
                  Cargando...
                </div>
              ) : !receivedPage || receivedPage.items.length === 0 ? (
                <div className="text-muted" style={{ padding: 8 }}>
                  Sin comprobantes en el período.
                </div>
              ) : (
                <>
                  <div style={{ overflowX: "auto" }}>
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th />
                          <th>Comprobante</th>
                          <th>Fecha</th>
                          <th className="text-center">IVA 10%</th>
                          <th className="text-center">IVA 5%</th>
                          <th style={{ width: 90 }}>Incluir</th>
                          <th style={{ width: 110 }}>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {receivedPage.items.map((d) => (
                          <ReceivedRow
                            key={`rd-${d.documentId}`}
                            doc={d}
                            consolidated={
                              receivedPage.consolidated || !canUpdateInclusions
                            }
                            onToggle={onToggleReceived}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <SectionPagination
                    page={pageNum.received}
                    total={receivedPage.total}
                    pageSize={receivedPage.pageSize}
                    onChange={(p) => setPageFor("received", p)}
                  />
                </>
              )}
            </div>
          </CCardBody>
        </CCard>
      )}

      <CCard>
        <CCardHeader>
          <strong>Histórico de períodos consolidados</strong>{" "}
          <CTooltip content="Los valores consolidados se generan automáticamente el primer día del mes siguiente al mes de presentación. Por ejemplo, el IVA de marzo se presenta en abril y el 1 de mayo se ejecuta el proceso que genera el valor consolidado del período.">
            <i
              className="fa fa-info-circle text-info"
              style={{ cursor: "help", marginLeft: 4 }}
            ></i>
          </CTooltip>
        </CCardHeader>
        <CCardBody>
          <CDataTable
            noItemsView={{
              noItems: "No se encontraron registros",
              noResults: "No se encontraron registros",
            }}
            items={historyRows.map((h) => ({
              periodo: `${MONTH_LABELS[h.month - 1]} ${h.year}`,
              debito: fmt((h.debit10 ?? 0) + (h.debit5 ?? 0)),
              credito: fmt((h.credit10 ?? 0) + (h.credit5 ?? 0)),
              neto: fmt(h.netIvaPayable),
              consolidadoAt: new Date(h.consolidatedAt).toLocaleString("es-PY"),
            }))}
            fields={[
              { key: "periodo", label: "Período" },
              { key: "debito", label: "Débito Fiscal" },
              { key: "credito", label: "Crédito Fiscal" },
              { key: "neto", label: "IVA a Pagar" },
              { key: "consolidadoAt", label: "Consolidado" },
            ]}
            hover
            striped
            border
          />
        </CCardBody>
      </CCard>
    </CContainer>
  );
};

export default IvaPage;
